import { CaregiverSelectionService } from './../caregiver-scoreboard/caregiver-selection/caregiver-selection-service/caregiver-selection.service';
import { RequestRecordFormComponent } from '../../shared/components/request-rounds/request-record-form/request-record-form.component';
import { RequestRecordService } from './request-record-service/request-record.service';
import { RoundB2bTableComponent } from './../../shared/components/request-rounds/round-b2b-table/round-b2b-table.component';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import {
  HoursComputedInterface,
  RequestRecord,
} from 'src/app/shared/models/request-round/request-record.interface';
import {
  ColfRequestDetail,
  Request,
  RequestNurseDetail,
  RequestPetsitterDetail,
} from 'npx-family-happy-common';
import { CaregiverRoundDTO } from 'src/app/shared/models/caregiver.model';
import { RequestStateHandlerComponent } from '../../shared/components/request-state-handler/request-state-handler.component';
import { RequestService } from '../request-service/request.service';
import { RequestPetsitterService } from '../request-petsitter-service/request-petsitter.service';
import { RequestNurseService } from '../request-nurse-service/request-nurse.service';
import { RequestColfService } from '../request-colf-service/request-colf.service';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { LongevityRequestAssessmentsListComponent } from './longevity-request-assessments/longevity-request-assessments-list.component';
import { RequestChatComponent } from './request-chat/request-chat.component';
import { RequestChatMessage, WithRequestChat } from './request-chat/request-chat.model';

type ContinuousServiceDetail = { continuousJob: boolean; estimatedLasting: string };

@Component({
  selector: 'app-request-round',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    ReactiveFormsModule,
    RoundB2bTableComponent,
    RequestRecordFormComponent,
    RequestStateHandlerComponent,
    LongevityRequestAssessmentsListComponent,
    RequestChatComponent,
  ],
  templateUrl: './request-round.component.html',
  styleUrl: './request-round.component.scss',
})
export class RequestRoundComponent implements OnInit, OnChanges, OnDestroy {
  @Input() request!: Request;
  @Input() assignedNutritionist!: string | null;
  @Input() petsitterDetail?: RequestPetsitterDetail;
  @Input() nurseDetail?: RequestNurseDetail;
  @Input() colfDetail?: ColfRequestDetail;
  @ViewChild(RequestStateHandlerComponent) private stateHandler?: RequestStateHandlerComponent;
  private unsubscribe$ = new Subject<void>();
  showNewDepForm = false;
  caregiverDTO!: CaregiverRoundDTO;
  records$ = new Subject<RequestRecord[]>();
  hoursAmount!: number;
  paymentURL!: string;
  totalHoursWorked!: number;
  hoursComputed!: HoursComputedInterface;

  continuousServiceForm = new FormGroup({
    continuousJob: new FormControl(false),
    estimatedLasting: new FormControl<string | null>(null),
  });
  hasContinuousServiceChanges = false;

  constructor(
    private recordService: RequestRecordService,
    private caregiverSelectionService: CaregiverSelectionService,
    private requestService: RequestService,
    private requestPetsitterService: RequestPetsitterService,
    private requestNurseService: RequestNurseService,
    private requestColfService: RequestColfService,
    private toast: ToastService
  ) {}

  get isContinuousServiceSupported(): boolean {
    return ['BABYSITTER', 'PETSITTER', 'NURSE', 'COLF'].includes(
      this.request.requestPersonalInfo.requestCategory
    );
  }

  /** Longevity non ha eventi né professionisti: il Registro mostra l'elenco dei test invece degli eventi. */
  get isLongevity(): boolean {
    return this.request.requestPersonalInfo.requestCategory === 'LONGEVITY';
  }

  /** Il Supporto caregiver non ha eventi né professionisti: il Registro mostra la chat con la persona. */
  get isCaregiverConsult(): boolean {
    return this.request.requestPersonalInfo.requestCategory === 'CAREGIVER_CONSULT';
  }

  get chatMessages(): RequestChatMessage[] | null | undefined {
    return (this.request as unknown as WithRequestChat).chat;
  }

  /**
   * La chat ha cambiato (o ha trovato cambiato) lo stato della richiesta: si aggiorna lo stato sull'oggetto
   * condiviso con il dettaglio, altrimenti un successivo "Salva" lo riporterebbe al valore vecchio, e il
   * selettore "Stato della richiesta" mostrato qui sotto.
   */
  onChatStatusChanged(status: string): void {
    this.request.operationalInfo.status = status;
    this.stateHandler?.syncStatus();
  }

  ngOnInit(): void {
    this.resetContinuousServiceForm();
    this.continuousServiceForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.hasContinuousServiceChanges = true;
      });
    if (this.isLongevity || this.isCaregiverConsult) {
      return;
    }
    console.log('Request in RequestRoundComponent:', this.request);
    this.getAllReqRecords();
    if (this.request.requestPersonalInfo.requestCategory === 'NUTRITIONIST') {
      this.caregiverDTO = {
        caregiverId: this.assignedNutritionist,
        caregiverName: this.assignedNutritionist,
      };
    } else {
      this.caregiverSelectionService
        .getCaregiverByRequestID(
          this.request.id,
          this.request.requestPersonalInfo.latitude,
          this.request.requestPersonalInfo.longitude
        )
        .pipe(
          takeUntil(this.unsubscribe$),
          map((caregiver) => {
            caregiver.forEach((c) => {
              if (c.chosen && !c.discarded) {
                let caregiverName =
                  c.babysitter?.info.name ??
                  c.petsitter?.info.name ??
                  c.nurse?.info.name ??
                  c.colf?.info.name ??
                  null;
                let caregiverSurname =
                  c.babysitter?.info.surname ??
                  c.petsitter?.info.surname ??
                  c.nurse?.info.surname ??
                  c.colf?.info.surname ??
                  null;
                let caregiverFullName = `${caregiverName!} ${caregiverSurname!}`;
                let caregiverRoundDTO: CaregiverRoundDTO = {
                  caregiverId:
                    c.babysitter?.docRef ??
                    c.petsitter?.docRef ??
                    c.nurse?.docRef ??
                    c.colf?.docRef ??
                    null,
                  caregiverName: caregiverFullName,
                };
                this.caregiverDTO = caregiverRoundDTO;
              }
            });
          })
        )
        .subscribe();
    }
  }

  getAllReqRecords() {
    this.records$.next([]);
    this.recordService
      .getAllRequestRecords(this.request.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((records) => {
          this.totalHoursWorked = records.reduce(
            (total, record) => total + (record.hoursWorked ?? 0),
            0
          );
          this.hoursComputed = {
            totalhourAmount: (this.hoursAmount =
              this.request.requestDetail?.hoursAmount),
            totalHoursWorked: this.totalHoursWorked,
          };
          this.records$.next(
            records.sort(
              (a, b) =>
                new Date(b.startDate).getTime() -
                new Date(a.startDate).getTime()
            )
          );
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Il dettaglio specifico per tipo (petsitter/nurse/colf) arriva in modo
    // asincrono dal componente padre: quando cambia, riallineiamo il form
    // solo se l'utente non ha già iniziato a modificarlo.
    if (
      !this.hasContinuousServiceChanges &&
      (changes['petsitterDetail'] || changes['nurseDetail'] || changes['colfDetail'] || changes['request'])
    ) {
      this.resetContinuousServiceForm();
    }
  }

  /**
   * Per BABYSITTER, continuousJob/estimatedLasting vivono su request.requestDetail
   * (endpoint generico). Per PETSITTER/NURSE/COLF vivono invece su un record
   * separato, gestito da endpoint dedicati (/v1/{tipo}-request/{id}) e già
   * mostrato/salvato nella sezione "Dettagli richiesta" per quei tipi.
   */
  private getContinuousServiceDetail(): ContinuousServiceDetail | null {
    switch (this.request.requestPersonalInfo.requestCategory) {
      case 'BABYSITTER':
        return this.request.requestDetail ?? null;
      case 'PETSITTER':
        return this.petsitterDetail ?? null;
      case 'NURSE':
        return this.nurseDetail ?? null;
      case 'COLF':
        return this.colfDetail ?? null;
      default:
        return null;
    }
  }

  private resetContinuousServiceForm(): void {
    const detail = this.getContinuousServiceDetail();
    if (!detail) {
      return;
    }
    this.continuousServiceForm.setValue(
      {
        continuousJob: detail.continuousJob ?? false,
        estimatedLasting: detail.estimatedLasting || null,
      },
      { emitEvent: false }
    );
  }

  saveContinuousService(): void {
    if (!this.getContinuousServiceDetail()) {
      return;
    }
    const value = this.continuousServiceForm.value;
    const continuousJob = value.continuousJob ?? false;
    const estimatedLasting = value.estimatedLasting ?? '';

    let save$: Observable<unknown>;
    let applyLocally: () => void;

    switch (this.request.requestPersonalInfo.requestCategory) {
      case 'PETSITTER': {
        const updated: RequestPetsitterDetail = { ...this.petsitterDetail!, continuousJob, estimatedLasting };
        save$ = this.requestPetsitterService.updatePetsitterDetail(this.request.id, updated);
        applyLocally = () => {
          this.petsitterDetail!.continuousJob = continuousJob;
          this.petsitterDetail!.estimatedLasting = estimatedLasting;
        };
        break;
      }
      case 'NURSE': {
        const updated: RequestNurseDetail = { ...this.nurseDetail!, continuousJob, estimatedLasting };
        save$ = this.requestNurseService.updateNurseDetail(this.request.id, updated);
        applyLocally = () => {
          this.nurseDetail!.continuousJob = continuousJob;
          this.nurseDetail!.estimatedLasting = estimatedLasting;
        };
        break;
      }
      case 'COLF': {
        const updated: ColfRequestDetail = { ...this.colfDetail!, continuousJob, estimatedLasting };
        save$ = this.requestColfService.updateColfDetail(this.request.id, updated);
        applyLocally = () => {
          this.colfDetail!.continuousJob = continuousJob;
          this.colfDetail!.estimatedLasting = estimatedLasting;
        };
        break;
      }
      default: {
        const updatedRequest: Request = {
          ...this.request,
          requestDetail: { ...this.request.requestDetail, continuousJob, estimatedLasting },
        };
        save$ = this.requestService.updateRequest(updatedRequest);
        applyLocally = () => {
          this.request.requestDetail.continuousJob = continuousJob;
          this.request.requestDetail.estimatedLasting = estimatedLasting;
        };
        break;
      }
    }

    save$
      .pipe(
        takeUntil(this.unsubscribe$),
        map(() => {
          applyLocally();
          this.hasContinuousServiceChanges = false;
          this.toast.setAction('Servizio continuativo aggiornato con successo', true);
        }),
        catchError(() => {
          this.toast.setAction(
            "Errore durante l'aggiornamento del servizio continuativo",
            false
          );
          return of(null);
        })
      )
      .subscribe();
  }

  openDepForm() {
    this.showNewDepForm = !this.showNewDepForm;
  }

  deleteRecord() {
    this.getAllReqRecords();
  }

  updateList() {
    this.getAllReqRecords();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
